import { useState, useCallback } from 'react';
import { Match, Player } from '../types';

interface OptimisticUpdates {
  players: { [playerId: string]: any };
  match: any;
  pendingOperations: { [key: string]: boolean };
}

interface OptimisticUpdateResult {
  confirm: () => void;
  rollback: () => void;
}

interface UseOptimisticUpdatesProps {
  match: Match | null;
  onSetMatch: (match: Match) => void;
}

interface UseOptimisticUpdatesReturn {
  optimisticUpdates: OptimisticUpdates;
  setOptimisticUpdates: React.Dispatch<React.SetStateAction<OptimisticUpdates>>;
  applyOptimisticUpdate: (operation: string, updateFn: () => void, rollbackFn: () => void) => OptimisticUpdateResult;
  getOptimisticMatch: () => Match | null;
  getOptimisticPlayer: (playerId: string) => Player | null;
  clearOptimisticUpdates: () => void;
  hasPendingOperations: () => boolean;
  getPendingOperations: () => string[];
}

export const useOptimisticUpdates = ({ match, onSetMatch }: UseOptimisticUpdatesProps): UseOptimisticUpdatesReturn => {
  const [optimisticUpdates, setOptimisticUpdates] = useState<OptimisticUpdates>({
    players: {},
    match: null,
    pendingOperations: {}
  });

  // Aplicar update optimista
  const applyOptimisticUpdate = useCallback((
    operation: string, 
    updateFn: () => void, 
    rollbackFn: () => void
  ): OptimisticUpdateResult => {
    // Marcar operación como pendiente
    setOptimisticUpdates(prev => ({
      ...prev,
      pendingOperations: { ...prev.pendingOperations, [operation]: true }
    }));

    // Aplicar cambio optimista
    updateFn();

    // Retornar función para confirmar o revertir
    return {
      confirm: () => {
        setOptimisticUpdates(prev => ({
          ...prev,
          pendingOperations: { ...prev.pendingOperations, [operation]: false }
        }));
      },
      rollback: () => {
        rollbackFn();
        setOptimisticUpdates(prev => ({
          ...prev,
          pendingOperations: { ...prev.pendingOperations, [operation]: false }
        }));
      }
    };
  }, []);

  // Obtener match optimista
  const getOptimisticMatch = useCallback((): Match | null => {
    if (!match) return null;
    
    // Aplicar todos los cambios optimistas pendientes
    let optimisticMatch = { ...match };
    
    // Aplicar cambios de jugadores
    Object.entries(optimisticUpdates.players).forEach(([playerId, changes]) => {
      const playerIndex = optimisticMatch.players.findIndex(p => p.id === playerId);
      if (playerIndex !== -1) {
        optimisticMatch.players[playerIndex] = { ...optimisticMatch.players[playerIndex], ...changes };
      }
    });
    
    // Aplicar cambios del match
    if (optimisticUpdates.match) {
      optimisticMatch = { ...optimisticMatch, ...optimisticUpdates.match };
    }
    
    return optimisticMatch;
  }, [match, optimisticUpdates]);

  // Obtener jugador optimista
  const getOptimisticPlayer = useCallback((playerId: string): Player | null => {
    const basePlayer = match?.players.find(p => p.id === playerId);
    if (!basePlayer) return null;
    
    const optimisticChanges = optimisticUpdates.players[playerId];
    if (!optimisticChanges) return basePlayer;
    
    return { ...basePlayer, ...optimisticChanges };
  }, [match, optimisticUpdates]);

  // Limpiar updates optimistas
  const clearOptimisticUpdates = useCallback(() => {
    setOptimisticUpdates({
      players: {},
      match: null,
      pendingOperations: {}
    });
  }, []);

  // Verificar si hay operaciones pendientes
  const hasPendingOperations = useCallback((): boolean => {
    return Object.values(optimisticUpdates.pendingOperations).some(pending => pending);
  }, [optimisticUpdates.pendingOperations]);

  // Obtener operaciones pendientes
  const getPendingOperations = useCallback((): string[] => {
    return Object.entries(optimisticUpdates.pendingOperations)
      .filter(([_, pending]) => pending)
      .map(([operation, _]) => operation);
  }, [optimisticUpdates.pendingOperations]);

  return {
    optimisticUpdates,
    setOptimisticUpdates,
    applyOptimisticUpdate,
    getOptimisticMatch,
    getOptimisticPlayer,
    clearOptimisticUpdates,
    hasPendingOperations,
    getPendingOperations
  };
};
